import { useState } from "react";
import { useAuth, apiClient } from "../App";
import { toast } from "sonner";
import { ShieldCheck, SignOut } from "@phosphor-icons/react";
import DobField, { formatDobLong } from "./DobField";

/**
 * AgeGate — calm, dignified 18+ self-declaration gate for logged-in users who
 * have not yet confirmed their age (e.g. legacy accounts). New users confirm at
 * signup. Privacy-preserving: only records that the user is old enough.
 * Posts to /auth/confirm-age, then refreshes the user so ProtectedRoute proceeds.
 */
export default function AgeGate() {
  const { refreshUser, logout } = useAuth();
  const [dob, setDob] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!confirmed || loading) return;
    setLoading(true);
    try {
      await apiClient.post("/auth/confirm-age", {
        age_confirmed_18_plus: true,
        date_of_birth: dob || null,
      });
      await refreshUser();
      toast.success("Welcome to ETHOS");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "We couldn't confirm that. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[600] overflow-y-auto flex flex-col items-center justify-center px-6"
      style={{
        background: "linear-gradient(165deg,#1A0E2E 0%,#2D1B4E 55%,#1A0E2E 100%)",
        paddingTop: "max(env(safe-area-inset-top,0px),24px)",
        paddingBottom: "max(env(safe-area-inset-bottom,0px),24px)",
      }}
      data-testid="age-gate"
    >
      <div className="w-full max-w-sm">
        {/* Emblem */}
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6"
          style={{ background: "rgba(196,163,224,0.14)", border: "1px solid rgba(196,163,224,0.25)" }}
        >
          <ShieldCheck size={26} weight="duotone" style={{ color: "#C4A3E0" }} />
        </div>

        <h1 className="text-center text-[22px] font-semibold leading-snug mb-2" style={{ color: "#F3ECFA" }}>
          An adults-only sanctuary
        </h1>
        <p className="text-center text-[13px] leading-relaxed mb-5" style={{ color: "rgba(228,212,248,0.72)" }}>
          ETHOS is an adults-only sanctuary for honest, values-first connection.
          Trust begins at the threshold.
        </p>

        {/* Trust Covenant — calm, values-first principles */}
        <div
          className="rounded-2xl px-4 py-3.5 mb-6"
          style={{ background: "rgba(196,163,224,0.08)", border: "1px solid rgba(196,163,224,0.18)" }}
          data-testid="age-gate-covenant"
        >
          <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: "rgba(196,163,224,0.78)" }}>
            Trust begins here
          </p>
          <ul className="space-y-1.5">
            {[
              "I am 18 years or older.",
              "My age and profile information are truthful.",
              "I respect consent, boundaries, and no-contact requests.",
              "ETHOS is for dignity-first adult connection.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-[12px] leading-snug" style={{ color: "rgba(228,212,248,0.84)" }}>
                <span style={{ color: "#C4A3E0" }}>✦</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* DOB (optional) — locale-independent Day / Month / Year */}
        <label className="block text-[11px] font-medium mb-1.5" style={{ color: "rgba(228,212,248,0.70)" }}>
          Date of birth <span style={{ color: "rgba(196,163,224,0.55)" }}>(optional)</span>
        </label>
        <DobField value={dob} onChange={setDob} theme="dark" testId="age-gate-dob" />
        {formatDobLong(dob) && (
          <p className="text-[11px] mt-1.5 mb-2" style={{ color: "rgba(196,163,224,0.70)" }} data-testid="age-gate-dob-readout">
            {formatDobLong(dob)}
          </p>
        )}
        <div className="mb-4" />

        {/* Required confirmation */}
        <label className="flex items-start gap-2.5 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded flex-shrink-0 accent-[#9060C8]"
            data-testid="age-gate-confirm-checkbox"
          />
          <span className="text-[12px] leading-relaxed" style={{ color: "rgba(228,212,248,0.85)" }}>
            By entering, I confirm my age is <strong>truthful</strong>, that I am <strong>18 or older</strong>, and that I am legally permitted to use ETHOS.
          </span>
        </label>

        {/* Privacy note */}
        <p className="text-[11px] leading-relaxed mb-6" style={{ color: "rgba(196,163,224,0.62)" }}>
          ETHOS only needs to know that you are old enough to enter — we are not asking
          for ID in this step. Misrepresenting your age or identity harms the safety of
          the community. Privacy-preserving age assurance may be required in future.
        </p>

        <button
          onClick={submit}
          disabled={!confirmed || loading}
          className="w-full h-12 rounded-full font-semibold text-sm transition-all disabled:opacity-40"
          style={{ background: confirmed ? "linear-gradient(135deg,#9060C8,#6B4E8A)" : "rgba(196,163,224,0.12)", color: "#FFFFFF" }}
          data-testid="age-gate-continue-btn"
        >
          {loading ? "Confirming…" : "Enter ETHOS"}
        </button>

        <button
          onClick={logout}
          className="w-full h-11 mt-2 rounded-full text-[13px] flex items-center justify-center gap-1.5"
          style={{ color: "rgba(228,212,248,0.55)" }}
          data-testid="age-gate-logout-btn"
        >
          <SignOut size={15} /> Not now — log out
        </button>
      </div>
    </div>
  );
}
